import { Box, IconButton } from "@chakra-ui/react"
import type { MouseEventHandler } from "react"
import { toaster } from "../chakra/ui/toaster"
import { pick } from "../filestack/api"
import Pathicon from "../pathicon/Pathicon"
import UserAvatar from "./UserAvatar"
import { getImage } from "./utils"

type AvatarImagePickerBaseProps = {
  avatar?: string | null
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  isDisabled?: boolean
  isLoading?: boolean
  onChange: (avatar: string) => void
  onPickAvatar?: MouseEventHandler<HTMLElement>
}

export function AvatarImagePicker({
  avatar,
  firstName,
  lastName,
  fullName,
  isDisabled,
  isLoading,
  onChange,
  onPickAvatar
}: AvatarImagePickerBaseProps) {
  const user = {
    id: 0,
    first_name: firstName || "",
    last_name: lastName || "",
    full_name: fullName || [firstName, lastName].filter(Boolean).join(" "),
    profile: {
      image: avatar || null
    }
  }
  const image = getImage(user)

  const handlePickAvatar: MouseEventHandler<HTMLElement> = async (event) => {
    if (isDisabled) return

    if (onPickAvatar) {
      onPickAvatar(event)
      return
    }

    const result = await pick({
      imageMax: [2000, 2000],
      maxFiles: 1,
      uploadInBackground: false,
      fromSources: [
        "local_file_system",
        "dropbox",
        "googledrive",
        "onedrive",
        "facebook",
        "unsplash",
        "imagesearch",
        "webcam",
        "instagram"
      ],
      maxSize: 100 * 1024 * 1024,
      accept: ["image/*", ".heic"]
    })

    const { filesUploaded, filesFailed } = result
    const url = filesUploaded?.[0]?.url

    if (url) {
      onChange(url)
    } else if (filesFailed?.length) {
      toaster.create({
        title: `Error uploading file: ${filesFailed[0].filename}`,
        type: "error",
        duration: 3000,
        closable: true
      })
    }
  }

  return (
    <Box pos="relative" w="max-content">
      <UserAvatar
        user={user}
        preferIcon
        bg={image ? "transparent" : "gray.200"}
        size="2xl"
        borderRadius="full"
        onClick={isDisabled ? undefined : handlePickAvatar}
        cursor={isDisabled ? "not-allowed" : "pointer"}
      />
      <IconButton
        aria-label={image ? "Change avatar" : "Upload avatar"}
        children={<Pathicon icon={image ? "shuffle" : "upload"} />}
        onClick={handlePickAvatar}
        h={8}
        minW={8}
        borderRadius="full"
        pos="absolute"
        top={0}
        right={0}
        transform="translate(25%,-25%)"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        variant="surface"
        disabled={isDisabled || isLoading}
        loading={isLoading}
      />
    </Box>
  )
}
